// Time utilities focused on America/Denver without external deps.
// Provides a 15-minute slot generator and conversions between local Denver time and UTC Date.

export type TimeSlot = {
  at: Date; // absolute UTC time corresponding to the Denver local wall time
  label: string; // e.g., "3:00"
};

const DENVER_TZ = 'America/Denver';

type YMDHM = { year: number; month: number; day: number; hour: number; minute: number };

function fmtParts(
  d: Date,
  opts: Intl.DateTimeFormatOptions
): Record<string, string> {
  const fmt = new Intl.DateTimeFormat('en-US', { timeZone: DENVER_TZ, ...opts });
  const rec: Record<string, string> = {};
  for (const p of fmt.formatToParts(d)) {
    if (p.type !== 'literal') rec[p.type] = p.value;
  }
  return rec;
}

export function getDenverParts(d: Date): YMDHM {
  const r = fmtParts(d, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
  return {
    year: Number(r.year),
    month: Number(r.month),
    day: Number(r.day),
    hour: Number(r.hour),
    minute: Number(r.minute),
  };
}

function sameYMD(a: { year: number; month: number; day: number }, b: { year: number; month: number; day: number }) {
  return a.year === b.year && a.month === b.month && a.day === b.day;
}

// Construct a Date that, when viewed in America/Denver, matches the provided Y/M/D H:M.
// Uses a two-pass correction to handle offsets without external libraries.
export function makeDateFromDenverParts(parts: YMDHM): Date {
  // Initial guess: interpret YMDHM as UTC
  let guess = new Date(Date.UTC(parts.year, parts.month - 1, parts.day, parts.hour, parts.minute, 0, 0));
  // Determine day delta (0 or ±1) between guess's Denver date and target
  const gParts = getDenverParts(guess);
  let dayDelta = 0;
  if (!sameYMD(gParts, parts)) {
    const gPlus = new Date(guess.getTime() + 24 * 60 * 60 * 1000);
    const gMinus = new Date(guess.getTime() - 24 * 60 * 60 * 1000);
    if (sameYMD(getDenverParts(gPlus), parts)) dayDelta = -1;
    else if (sameYMD(getDenverParts(gMinus), parts)) dayDelta = 1;
    else dayDelta = 0;
  }
  const currentLocalMins = gParts.hour * 60 + gParts.minute + dayDelta * 1440;
  const targetLocalMins = parts.hour * 60 + parts.minute;
  let corrected = new Date(guess.getTime() + (targetLocalMins - currentLocalMins) * 60 * 1000);
  // Second pass to ensure exact match
  const cParts = getDenverParts(corrected);
  if (!sameYMD(cParts, parts) || cParts.hour !== parts.hour || cParts.minute !== parts.minute) {
    const cCurrentMins = cParts.hour * 60 + cParts.minute + (sameYMD(cParts, parts) ? 0 : (cParts.day > parts.day ? 1 : -1) * 1440);
    corrected = new Date(corrected.getTime() + (targetLocalMins - cCurrentMins) * 60 * 1000);
  }
  return corrected;
}

function formatSlotLabel(d: Date): string {
  const fmt = new Intl.DateTimeFormat('en-US', {
    timeZone: DENVER_TZ,
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
  // Convert "3:00 PM" -> "3:00"
  const s = fmt.format(d);
  return s.replace(/\s?[AP]M$/i, '');
}

function ceilToNextQuarter(minute: number): number {
  return Math.min(45, Math.ceil(minute / 15) * 15 % 60);
}

// Generate 15-minute slots from the next quarter hour up to 8:00 PM Denver local.
// If current local time is after 7:45 PM, returns empty array.
export function generateLaterSlots(now: Date = new Date()): TimeSlot[] {
  const p = getDenverParts(now);
  // After 19:45 (exclusive), no later slots
  if (p.hour > 19 || (p.hour === 19 && p.minute > 45)) {
    return [];
  }
  let startHour = p.hour;
  let startMinute = ceilToNextQuarter(p.minute);
  if (startMinute === 0) {
    startHour = (startHour + 1);
  }
  // Build slots until 20:00 inclusive
  const slots: TimeSlot[] = [];
  let h = startHour;
  let m = startMinute === 0 ? 0 : startMinute;
  while (true) {
    if (h > 20 || (h === 20 && m > 0)) break;
    if (h === 20 && m === 0) {
      // 8:00 PM is allowed
      const at = makeDateFromDenverParts({ ...p, hour: 20, minute: 0 });
      slots.push({ at, label: formatSlotLabel(at) });
      break;
    }
    const at = makeDateFromDenverParts({ ...p, hour: h, minute: m });
    // Only future times
    if (at.getTime() > now.getTime()) {
      slots.push({ at, label: formatSlotLabel(at) });
    }
    m += 15;
    if (m >= 60) {
      m = 0;
      h += 1;
    }
  }
  return slots;
}

